/**
 * Section Partner - Récupération des collections partenaires via Storefront API
 * 
 * CONFIGURATION REQUISE:
 * 1. Créer une app Shopify privée avec accès Storefront API
 * 2. Permissions nécessaires: unauthenticated_read_product_listings, unauthenticated_read_metaobjects
 * 3. Copier le Storefront Access Token
 * 4. Ajouter window.STOREFRONT_ACCESS_TOKEN dans le code ci-dessous
 */

class PartnerCollections {
  constructor() {
    this.collections = [];
    this.listContainer = document.querySelector('.partner-list');
    
    // Configuration du token - À REMPLIR avec votre token Storefront API
    this.storefrontToken = window.STOREFRONT_ACCESS_TOKEN || '';
    
    if (this.listContainer) {
      this.init();
    }
  }

  async init() {
    if (!this.storefrontToken) {
      this.showError('Configuration requise: Storefront Access Token manquant.');
      return;
    }
    
    try {
      await this.fetchAllCollections();
      this.renderCollections();
    } catch (error) {
      this.showError();
    }
  }

  /**
   * Récupère toutes les collections avec pagination
   */
  async fetchAllCollections(cursor = null) {
    const query = `{
      collections(first: 250${cursor ? `, after: "${cursor}"` : ''}) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title
            handle
            metafield(namespace: "custom", key: "page_partenaires") {
              value
            }
          }
        }
      }
    }`;

    const response = await fetch('/api/2024-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontToken
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error('GraphQL query failed');
    }

    const collections = data.data.collections;
    
    // Filtrer les collections avec page_partenaires = true
    const partnerCollections = collections.edges
      .filter(edge => {
        const metafieldValue = edge.node.metafield?.value;
        return metafieldValue === 'true' || metafieldValue === true;
      })
      .map(edge => ({
        title: edge.node.title,
        handle: edge.node.handle,
        url: `/collections/${edge.node.handle}`
      }));

    this.collections = [...this.collections, ...partnerCollections];

    // Si il y a d'autres pages, continuer la récupération
    if (collections.pageInfo.hasNextPage) {
      await this.fetchAllCollections(collections.pageInfo.endCursor);
    }
  }

  /**
   * Trie et affiche les collections
   */
  renderCollections() {
    if (!this.listContainer) return;

    // Trier par ordre alphabétique
    const sortedCollections = this.collections.sort((a, b) => 
      a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' })
    );

    // Vider le loader
    this.listContainer.innerHTML = '';

    // Afficher les collections
    if (sortedCollections.length === 0) {
      this.showEmptyMessage();
      return;
    }

    sortedCollections.forEach(collection => {
      const li = document.createElement('li');
      li.className = 'partner-collection__item';
      
      const link = document.createElement('a');
      link.href = collection.url;
      link.className = 'partner-collection__link';
      link.textContent = collection.title;
      
      li.appendChild(link);
      this.listContainer.appendChild(li);
    });
  }

  /**
   * Affiche un message d'erreur
   */
  showError(customMessage = null) {
    if (!this.listContainer) return;
    
    const message = customMessage || 'Une erreur est survenue lors du chargement des collections.';
    
    this.listContainer.innerHTML = `
      <li style="color: #e53e3e; list-style: none;">
        ${message}
      </li>
    `;
  }

  /**
   * Affiche un message si aucune collection
   */
  showEmptyMessage() {
    if (!this.listContainer) return;
    
    this.listContainer.innerHTML = `
      <li style="list-style: none; color: #6b7280;">
        Aucune collection partenaire disponible pour le moment.
      </li>
    `;
  }
}

// Initialisation quand le DOM est prêt
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PartnerCollections();
  });
} else {
  new PartnerCollections();
}

